import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import ContextualIcon from "@redotech/redo-icons/contextual.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { memo } from "react";
import { matchPath, useLocation } from "react-router-dom";

export const ContextualItems = memo(function ContextualItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const location = useLocation();

  const contextualSettings = team.settings.contextual;
  const isEnabled =
    !!contextualSettings?.enabled || !!contextualSettings?.visible;

  // TODO: Replace with RPC query for onboarding status
  const onboardingCompleted = false;

  const contextualRoot = routes.contextualHref;
  const anyChildSelected = !!matchPath(
    `${contextualRoot}/*`,
    location.pathname,
  );

  return (
    <>
      <ContextualSetupItem
        anyChildSelected={anyChildSelected}
        hidden={!isEnabled || onboardingCompleted}
      />
      <ContextualFolderItem
        anyChildSelected={anyChildSelected}
        hidden={!isEnabled || !onboardingCompleted}
      />
    </>
  );
});

/** Flat nav item shown while onboarding is not complete. */
const ContextualSetupItem = memo(function ContextualSetupItem({
  anyChildSelected,
  hidden,
}: {
  anyChildSelected: boolean;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.contextualHref }}
      anyChildSelected={anyChildSelected}
      FilledIcon={ContextualIcon}
      friendlyName={t("Contextual", {
        context: "marketing navigation label for contextual shopper messaging",
      })}
      hidden={hidden}
      id="contextual-setup"
      UnfilledIcon={ContextualIcon}
    />
  );
});

/** Dropdown with children shown once onboarding is complete. */
const ContextualFolderItem = memo(function ContextualFolderItem({
  anyChildSelected,
  hidden,
}: {
  anyChildSelected: boolean;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getContextualSubRoute("dashboard") }}
      anyChildSelected={anyChildSelected}
      FilledIcon={ContextualIcon}
      friendlyName={t("Contextual", {
        context: "marketing navigation label for contextual shopper messaging",
      })}
      hidden={hidden}
      id="contextual"
      UnfilledIcon={ContextualIcon}
    >
      <ContextualDashboardItem />
    </RedoSecondaryNavItem>
  );
});

const ContextualDashboardItem = memo(function ContextualDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getContextualSubRoute("dashboard") }}
      friendlyName={t("Dashboard", {
        context: "merchant marketing navigation label",
      })}
      id="contextual-dashboard"
    />
  );
});
